import { computed, type Ref } from 'vue'
import { useTranslation } from 'i18next-vue'
import type { Chapter } from '@/views/layouts/types'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'
import type { Company } from '@/types/company'
import { ORGANIZER_FORMAT_INSTITUTIONS_ROUTE } from '@/router/organizer/organizer-route-names.ts'

/**
 * Composable for managing format chapters in the chapterized layout.
 * Provides a centralized definition of all chapters with their routes and labels.
 *
 * @param formatId - The document ID of the format (can be '@new' for new formats)
 * @returns Computed array of all available chapters
 */
export function useFormatChapters(formatId: Ref<string | undefined>) {
    const { t } = useTranslation()

    const chapters = computed((): Chapter[] => {
        const formatDocumentId = formatId.value
        const authStore = useAuthStore()
        const company = computed<Company>(() => authStore.organizer?.company as Company)
        const allChapter: Chapter[] = [
            {
                name: 'company',
                label: t('ui.chapters.company'),
                to: {
                    name: 'organizer-company-form',
                    params: { formatId: formatDocumentId },
                },
                // WHY: Keep the company chapter visible until required profile fields
                // are complete, so organizers finish mandatory company metadata first.
                condition: () => {
                    const c = company.value
                    if (!c) return true
                    return !c.logo
                },
            },

            {
                name: 'institutions',
                label: t('ui.chapters.institutions'),
                to: {
                    name: ORGANIZER_FORMAT_INSTITUTIONS_ROUTE,
                    params: {
                        formatId: formatDocumentId,
                    },
                },
            },
            {
                name: 'format',
                label: t('ui.chapters.format'),
                to: {
                    name: 'format-format-form',
                    params: { formatId: formatDocumentId },
                },
            },
            {
                name: 'events',
                label: t('ui.chapters.events'),
                to: {
                    name: 'organizer-format-events',
                    params: { formatId: formatDocumentId },
                },
                matchesRoutes: ['organizer-format-event-form'],
                isOverview: true,
            },
            {
                name: 'job-postings',
                label: t('ui.chapters.jobPostings'),
                to: {
                    name: 'organizer-format-job-postings',
                    params: { formatId: formatDocumentId },
                },
                matchesRoutes: ['organizer-format-job-posting-form'],
                isOverview: true,
                finishLabel: t('ui.jobPostings.sections.actions.finalizeProfile'),
            },
            {
                name: 'applications',
                label: t('ui.chapters.applications'),
                matchesRoutes: ['format-application-form'],
                isOverview: true,
                to: {
                    name: 'format-application-preview',
                    params: { formatId: formatDocumentId },
                },
            },
            {
                name: 'schedule',
                label: t('ui.chapters.schedule'),
                to: {
                    name: 'organizer-format-schedule-start',
                    params: { formatId: formatDocumentId },
                },
                matchesRoutes: ['organizer-format-schedule'],
            },

            {
                name: 'legal-forms',
                label: t('ui.chapters.legal'),
                to: {
                    name: 'format-legal-form',
                    params: { formatId: formatDocumentId },
                },
            },
            {
                name: 'summary',
                label: t('ui.chapters.summary'),
                to: {
                    name: 'organizer-format-summary-start',
                    params: { formatId: formatDocumentId },
                },
                matchesRoutes: ['organizer-format-summary'],
            },
        ]
        return allChapter.filter((chapter) => (chapter.condition ? chapter.condition() : true))
    })

    const route = useRoute()
    const router = useRouter()

    // WHY: When a user is inside a detail route of an overview chapter, we return
    // to the overview list before advancing to the next chapter to preserve list context.
    const currentChapterNav = computed(() => {
        const currentName = route.name as string

        const index = chapters.value.findIndex(
            (chapter) =>
                chapter.to.name === currentName || chapter.matchesRoutes?.includes(currentName)
        )

        if (index === -1) {
            return {
                index: -1 as const,
                isBackToOverview: false,
                nextChapter: null,
                target: null,
            }
        }

        const chapter = chapters.value[index]
        const isBackToOverview =
            !!chapter.isOverview && chapter.matchesRoutes?.includes(currentName)

        const nextChapter = chapters.value[index + 1] ?? null
        const target = isBackToOverview ? chapter.to : (nextChapter?.to ?? null)

        return {
            index,
            isBackToOverview,
            nextChapter,
            target,
            currentChapter: chapter,
        }
    })

    const routeToNextChapter = () => {
        const { target } = currentChapterNav.value
        if (!target) return
        router.push(target)
    }

    const nextRouteLabel = computed(() => {
        const { index, isBackToOverview, nextChapter, currentChapter } = currentChapterNav.value
        if (index === -1) return null

        if (isBackToOverview) {
            return (
                currentChapter?.finishLabel ??
                t('ui.common.finishChapter', {
                    chapter: currentChapter?.label,
                })
            )
        }

        if (!nextChapter) return null

        return t('ui.common.nextTo', {
            route: nextChapter.label,
        })
    })
    return { chapters, routeToNextChapter, nextRouteLabel, currentChapterNav }
}
